'use client';

import React from 'react';
import Link from 'next/link';
import { MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

interface AuditorCardProps {
    id: string;
    firstName: string;
    lastName: string;
    city: string;
    country: string;
    profilePictureUrl?: string; // Optional full URL
}

export const AuditorCard = ({
    id,
    firstName,
    lastName,
    city,
    country,
    profilePictureUrl,
}: AuditorCardProps) => {
    return (
        <Link href={`/auditors/${id}`} className="block h-full">
            <motion.div
                className="bg-white border border-slate-200 h-full overflow-hidden flex flex-col group"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
            >
                {/* Image Section - Landscape 3:2 aspect ratio */}
                <div className="relative w-full h-[180px] bg-slate-100 overflow-hidden">
                    {profilePictureUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={profilePictureUrl}
                            alt={`${firstName} ${lastName}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                e.currentTarget.src = '/profile-placeholder.png';
                                e.currentTarget.classList.remove('group-hover:scale-105');
                            }}
                        />
                    ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src="/profile-placeholder.png"
                            alt={`${firstName} ${lastName}`}
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>

                {/* Content Section */}
                <div className="p-3 flex flex-col flex-grow">
                    <h3 className="text-base font-medium text-slate-900 mb-1 group-hover:underline decoration-1 underline-offset-4">
                        {firstName} {lastName}
                    </h3>

                    <div className="flex items-center text-xs text-slate-500 gap-1 mt-auto">
                        <MapPin className="w-3 h-3" />
                        <span>{city}, {country}</span>
                    </div>
                </div>
            </motion.div>
        </Link>
    );
};
